 import { PostNamespace, PostId, SubPostId } from '../types/post'

 import { CONFIG } from './CONFIG'

// WIP
type RouteNode = {
  path: string
  [key: string]: RouteNode | string | undefined
}

export const ROUTE_HIERARCHY: RouteNode = {
  path: '/',
  '/': {
    path: '/app',
    new: '/new',
    create: '/create',
    view: {
      path: '/view',
      by_id: '/:id',
      by_namespace: '/:namespace',
      post: {
        path: '/post',
        by_id: '/:id',
        sub_id: '/:id/:subId',
      },
    },
    edit: {
      path: '/edit',
      by_id: '/:id',
      by_namespace: '/:namespace',
    },
    user: {
      path: '/user',
      by_id: '/:uid',
    },
    uri: {
      path: '/uri',
      by_id: '/:id',
    },
    peek: {
      path: '/peek',
      by_id: '/:id',
      id: {
        path: '/id',
        by_id: '/:id',
      },
    },
    profile: {
      path: '/profile',
      by_id: '/:id',
    },
    tree: {
      path: '/tree',
      by_id: '/:id',
    },
  },
}

function getFullPath(route: RouteNode | string): string {
  if (typeof route === 'string') {
    return route
  }

  let fullPath = route.path || ''
  const keys = Object.keys(route).filter((key) =>
    key !== 'path')

  if (keys.length > 0) {
    const firstKey = keys[0]
    if (firstKey) {
      const firstChild = route[firstKey]
      if (firstChild !== undefined) {
        fullPath += getFullPath(firstChild)
      }
    }
  }

  return fullPath
}

function createDynamicPath(
  route: RouteNode | string,
  params: Record<string, string | number>
): string {
  let path = getFullPath(route)
  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`:${key}`, value.toString())
  })
  return path
}
// END WIP


export const _ROOT_ROUTE = '/' // Don't Change! Only use this for the actual rootpath. i.e. READ ONLY

// Use these for partial paths
export const ROUTE_NAMES = {
  id: 'id', // postId
  view: 'view', // namespace
  edit: 'edit', // edit root
  user: 'user', // userId

  uri: 'uri',
  peek: 'peek',

  profile: 'profile',
  tree: 'tree',
}

// Absolute paths
export const ROUTES = {
  app: `/${CONFIG.app}`,

  new: '/new',
  create: '/create',
  404: '/404',

  // ids
  id: `/${ROUTE_NAMES.id}`, // postId
  view: `/${ROUTE_NAMES.view}`, // namespace
  edit: `/${ROUTE_NAMES.edit}`, // edit root
  user: `/${ROUTE_NAMES.user}`, // userId

  // Admin Lists
  feed: '/feed',
  posts: '/posts',
  types: '/types',
  cats: '/cats',
  tags: '/tags',
  list: '/list',
  search: '/search', // Change manifest.json when changing this!

  uri: `/${ROUTE_NAMES.uri}`,

  // Lists
  filter: '/filter',
  namespaces: '/namespaces',
  urls: '/urls',
  uris: '/uris',
  tree: `/${ROUTE_NAMES.tree}`,
  users: '/users',

  peek: `/${ROUTE_NAMES.peek}`,

  // Type routes
  lists: '/lists',
  locations: '/locations',
  map: '/map',

  // Public
  name: '/@', // UserId

  popular: '/popular',
  judgements: '/judgements',
  invites: '/invites', // DODO

  home: `/${CONFIG.home}`,
  about: '/about',
  contact: '/contact',

  apply: '/apply',
  thanks: '/thanks',
  tou: '/terms-of-use',
  login: '/login',

  // Private
  profile: `/${ROUTE_NAMES.profile}`, // uid? (So you share /profile/uid?)

  // Misc
  test: '/test',

  flow: '/flow',
  snap: '/snap',
}

// Absolute query routes
export const QUERY_ROUTES = {
  view: [`/${ROUTE_NAMES.id}/:id`, `/${ROUTE_NAMES.view}/:namespace`], // Namespace/view id needs to be second for getHref to work
  edit: [`/${ROUTE_NAMES.edit}/${ROUTE_NAMES.id}/:id`, `/${ROUTE_NAMES.edit}/:namespace`], // Namespace/view id needs to be second for getHref to work

  editId: `/${ROUTE_NAMES.edit}/${ROUTE_NAMES.id}/:id`, // OLD

  viewPostId: `/${ROUTE_NAMES.view}/${ROUTE_NAMES.id}/:id`,

  viewId: `/${ROUTE_NAMES.id}/:id`,
  postId: `/post/${ROUTE_NAMES.id}/:id`,

  editNamespace: `/${ROUTE_NAMES.edit}/:namespace`,
  viewNamespace: `/${ROUTE_NAMES.view}/:namespace`,

  peekId: `/${ROUTE_NAMES.peek}/${ROUTE_NAMES.id}/:id`,
  peekNamespace: `/${ROUTE_NAMES.peek}/:namespace`,

  uriId: `/${ROUTE_NAMES.uri}/:id`,

  user: `/${ROUTE_NAMES.user}/:uid`,

  tree: `/${ROUTE_NAMES.tree}/:uid`,

  viewSubId: `/${ROUTE_NAMES.view}/post/${ROUTE_NAMES.id}/:id/${ROUTE_NAMES.id}/:subId`,
  viewIdSubNamespace: `/${ROUTE_NAMES.id}/:id/${ROUTE_NAMES.view}/:namespace`, // TODO
  viewNamespaceSubNamespace: `/${ROUTE_NAMES.view}/:namespace/${ROUTE_NAMES.view}/:namespace`, // TODO
  viewNamespaceSubId: `/${ROUTE_NAMES.view}/:namespace/${ROUTE_NAMES.id}/:id`, // TODO

  postNamespace: '/post/:namespace',

  viewObject: '/r/:namespace',
  viewObjectSub: '/r/:namespace/f/:subNamespace',

  flowViewId: '/flow/view/:id',
  flowId: '/flow/edit/:id',
  flowNamespace: '/flow/:namespace',
}

// For routes that use ROUTES vars
export const DEFAULT_ROUTES: Record<string, string> = {
  root: _ROOT_ROUTE, // WARNING: If you change this, use a trailing /, e.g. `/home/`
  drafts: `${ROUTE_NAMES.profile}/drafts`,
}

const TYPE_PAGES = [
  // TODO: ...(Object.keys(typeData).map(type => `/${type.plural ? type.plural : type.val}`)),
  // Type plurals
  '/terms',
  ROUTES.lists,
  '/texts',
  '/objects',
  '/media',
  '/entities',
  '/people',
  '/graphs',
  ROUTES.locations,
]

// NOTE: Make sure all possible system routes are included here
// Flat list of all possible system routes
export const ALL_ROUTES = [
  ...Object.values(ROUTE_NAMES),
  ...Object.values(ROUTES),
  ...Object.values(QUERY_ROUTES),
  ...Object.values(DEFAULT_ROUTES),
  ...TYPE_PAGES,
]

const stripQuery = (str: string) =>
  str.split('/:')[0]

// Query factories
export const RouteQueries = {

  editId: (postId: PostId) =>
    `${stripQuery(QUERY_ROUTES.editId)}/${postId}`, // OLD

  viewPost: (postId: PostId) =>
    `${stripQuery(QUERY_ROUTES.viewPostId)}/${postId}`,

  viewId: (postId: PostId) =>
    `${stripQuery(QUERY_ROUTES.viewId)}/${postId}`,

  editPost: (postId: PostId) =>
    `${stripQuery(QUERY_ROUTES.postId)}/${postId}`,

  editNamespace: (namespace: PostNamespace) =>
    `${stripQuery(QUERY_ROUTES.editNamespace)}/${namespace}`,

  viewNamespace: (namespace: PostNamespace) =>
    `${stripQuery(QUERY_ROUTES.viewNamespace)}/${namespace}`,

  peekId: (postId: string) =>
    `${stripQuery(QUERY_ROUTES.peekId)}/${postId}`,

  peekNamespace: (namespace: PostNamespace) =>
    `${stripQuery(QUERY_ROUTES.peekNamespace)}/${namespace}`,

  uriId: (uriId: string) =>
    `${stripQuery(QUERY_ROUTES.uriId)}/${uriId}`,

  // TODO: Use all paths from QUERY_ROUTES
  viewPostIdPeekId: (postId: PostId, subPostId: SubPostId) =>
    `${stripQuery(QUERY_ROUTES.viewSubId)}/${postId}/${
      ROUTE_NAMES.id
    }/${subPostId}`,

  flowEdit: (postId: string) =>
    `${stripQuery(QUERY_ROUTES.flowId)}/${postId}`,

  flowEditNamespace: (namespace: PostNamespace) =>
    `${stripQuery(QUERY_ROUTES.flowNamespace)}/${namespace}`,

  flowView: (postId: PostId) =>
    `${stripQuery(QUERY_ROUTES.flowViewId)}/${postId}`,
}

export const EPIC_ROUTES = {
  afterCreate: (postId: PostId) =>
    `/post/${ROUTE_NAMES.id}/${postId}`,

  afterRevert: (postId: PostId) =>
    `/${ROUTE_NAMES.edit}/${ROUTE_NAMES.id}/${postId}`,

  // afterLinkPost: (postId: string) => `/${ROUTE_NAMES.edit}/${ROUTE_NAMES.id}/${postId}`,

  afterApplication: () =>
    '/thanks?reason=application',

  // afterPublishNamespace: (namespace: string) => `/${ROUTE_NAMES.view}/${namespace}`,

  // afterPublishNoNamespace: (id: string) => `/${ROUTE_NAMES.edit}/${ROUTE_NAMES.id}/${id}`,

  // afterPublishViewId: (id: string) => `/${ROUTE_NAMES.id}/${id}`,
}

// Collections of namespaces, to use in different combinations to show/hide UI elements
const POST_ID_OR_NAMESPACE = [QUERY_ROUTES.postNamespace, QUERY_ROUTES.postId]

const EDIT_PAGES = [QUERY_ROUTES.editId, QUERY_ROUTES.editNamespace]

export const OBJECT_PAGES = [QUERY_ROUTES.viewObject, QUERY_ROUTES.viewObjectSub]

const SYSTEM_PAGES = [
  ROUTES.apply,
  ROUTES.tou,
  ROUTES.thanks
]

const LIST_PAGES = [
  ROUTES.posts,
  ROUTES.list,
  ROUTES.popular,
  ROUTES.users,
  ROUTES.tags,
  ROUTES.urls,
  ROUTES.uris,
  ROUTES.cats,
  ROUTES.types,
  ROUTES.namespaces,
  ROUTES.tree,
]

const BROWSE_PAGES = [
  ...LIST_PAGES,
  ...TYPE_PAGES,
  ...SYSTEM_PAGES,
  ROUTES.filter,
  ROUTES.test,
]

const POST_PAGES = [
  ...EDIT_PAGES,
  QUERY_ROUTES.viewId,
  QUERY_ROUTES.viewSubId,

  QUERY_ROUTES.viewNamespace,

  ...POST_ID_OR_NAMESPACE,
]

const CRUD_PAGES = [
  ROUTES.new,
  ROUTES.create,
  ...EDIT_PAGES
]

export const PEEK_PAGES = [QUERY_ROUTES.peekNamespace, QUERY_ROUTES.peekId]

const FILTER_PAGES = [
  ROUTES.profile,
  ROUTES.user,
  ROUTES.posts,
  // ROUTES.new,
  ROUTES.create,
  ROUTES.test,
  // ...postPages,
  ...LIST_PAGES,
]

// Define what UI elements are shown on which routes
const UI_ROUTES = {
  hideAppNav: [...OBJECT_PAGES, ROUTES.snap, '/nav'],
  menu: {
    hamburger: [
      _ROOT_ROUTE,
      ROUTES.profile,

      ROUTES.user,
      ROUTES.login,
      ROUTES.apply,
      ROUTES.thanks,

      ...BROWSE_PAGES,
      ...POST_ID_OR_NAMESPACE,
      ...PEEK_PAGES,

      ...QUERY_ROUTES.view,
      QUERY_ROUTES.viewPostId,

      QUERY_ROUTES.viewSubId,
    ],
    back: [QUERY_ROUTES.uriId, ...CRUD_PAGES],
  },
  topBar: {
    clearFilters: [
      ...FILTER_PAGES,
      ...TYPE_PAGES,
      ROUTES.filter,
      ROUTES.new,
      ROUTES.create,
    ],
    catSelector: [
      ...FILTER_PAGES,
      ...TYPE_PAGES,
      ROUTES.filter
    ],
    typeSelector: [
      ...FILTER_PAGES,
      ...TYPE_PAGES,
      ROUTES.filter
    ],
    filters: [
      ...FILTER_PAGES,
      ...TYPE_PAGES,
      ROUTES.filter,
      ROUTES.new,
      ROUTES.create,
      ROUTES.edit,
      QUERY_ROUTES.postId,
      QUERY_ROUTES.editId,
      QUERY_ROUTES.editNamespace,
    ],
    darkMode: [_ROOT_ROUTE, ROUTES.test],
    test: [process.env.NEXT_PUBLIC_ENV !== 'production' ? '*' : ''],
  },
  fab: {
    add: [
      _ROOT_ROUTE,
      ROUTES.posts,
      ROUTES.profile,
      ROUTES.lists,
      ROUTES.filter,
      QUERY_ROUTES.uriId,
    ],
    view: [QUERY_ROUTES.editId, QUERY_ROUTES.postId],
    visibilityNamespace: [QUERY_ROUTES.editNamespace],
    edit: [QUERY_ROUTES.viewPostId, ...QUERY_ROUTES.view],
    editNamespace: [QUERY_ROUTES.viewNamespace],
  },
}

export default UI_ROUTES
